import React, { useState } from 'react';

interface SyncButtonProps {
  onSync: () => Promise<void>;
  isSyncing?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const SyncButton: React.FC<SyncButtonProps> = ({ onSync, isSyncing = false, size = 'medium' }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = async () => {
    if (isSyncing) return;
    
    setIsAnimating(true);
    try {
      await onSync();
    } finally {
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const sizeStyles = {
    small: { width: '32px', height: '32px', fontSize: '14px' },
    medium: { width: '40px', height: '40px', fontSize: '18px' },
    large: { width: '48px', height: '48px', fontSize: '22px' },
  };

  const style = sizeStyles[size];

  const isDarkMode = document.documentElement.classList.contains('dark-mode');
  
  return (
    <button
      onClick={handleClick}
      disabled={isSyncing}
      className="sync-button-header"
      style={{
        ...style,
        borderRadius: '50%',
        border: 'none',
        background: isSyncing
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : isDarkMode ? '#2d2d2d' : '#e0e5ec',
        color: isSyncing ? 'white' : (isDarkMode ? '#e0e0e0' : '#1a202c'),
        cursor: isSyncing ? 'wait' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isSyncing
          ? '0 4px 12px rgba(102, 126, 234, 0.4)'
          : isDarkMode
            ? '6px 6px 12px rgba(0, 0, 0, 0.6), -6px -6px 12px rgba(0, 0, 0, 0.3)'
            : '6px 6px 12px rgba(163, 177, 198, 0.6), -6px -6px 12px rgba(255, 255, 255, 0.5)',
        transition: 'all 0.3s ease',
        transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
        padding: 0,
        position: 'relative',
      }}
      title={isSyncing ? 'Syncing...' : 'Sync data'}
      aria-label={isSyncing ? 'Syncing' : 'Sync'}
    >
      {isSyncing ? (
        <svg
          width={style.fontSize}
          height={style.fontSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            animation: 'spin 1s linear infinite',
          }}
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
        </svg>
      ) : (
        <svg
          width={style.fontSize}
          height={style.fontSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
        </svg>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default SyncButton;
